"use client"

import { FC, useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useSpacePageContextOptional } from "@/components/spaces/space-page-context"
import { useSpaceStatusTemplate } from "@/hooks/useSpaceStatusTemplate"
import { spaceMembersQueryOptions } from "@/lib/spaces/queries"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  IconEdit,
  IconTrash,
  IconCalendar,
  IconFolder,
  IconCopy,
  IconArrowsMove,
  IconAlertTriangle,
  IconCheck
} from "@tabler/icons-react"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { format } from "date-fns"
import { toast } from "sonner"

interface Task {
  id: string
  title: string
  description?: string
  status: 'todo' | 'in_progress' | 'done' | 'blocked' | 'cancelled'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  due_date?: string
  assigned_to?: string
  assigned_to_name?: string
  tags?: string[]
  folder_id?: string
  sprint_id?: string
}

interface BulkEditDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedTasks: Task[]
  spaceId: string
  onBulkUpdate?: (updates: Partial<Task>) => void | Promise<void>
  onBulkDelete?: (taskIds: string[]) => void | Promise<void>
  onBulkMove?: (taskIds: string[], destinationId: string) => void | Promise<void>
  onBulkCopy?: (taskIds: string[], destinationId: string) => void | Promise<void>
}

type BulkAction = 'edit' | 'delete' | 'move' | 'copy'

export const BulkEditDialog: FC<BulkEditDialogProps> = ({
  isOpen,
  onClose,
  selectedTasks,
  spaceId,
  onBulkUpdate,
  onBulkDelete,
  onBulkMove,
  onBulkCopy
}) => {
  const { availableStatuses, statusTemplate } = useSpaceStatusTemplate(spaceId)
  const spacePage = useSpacePageContextOptional()
  const [activeAction, setActiveAction] = useState<BulkAction>('edit')
  const [updates, setUpdates] = useState<Partial<Task>>({})
  const [fieldsToUpdate, setFieldsToUpdate] = useState<Set<string>>(new Set())
  const [destinationFolder, setDestinationFolder] = useState('')
  const [newTags, setNewTags] = useState('')
  const [tagAction, setTagAction] = useState<'add' | 'remove' | 'replace'>('add')
  const [dateToSet, setDateToSet] = useState<Date>()
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  const supportedActions = [
    {
      key: 'edit' as const,
      label: 'Edit',
      icon: IconEdit,
      description: 'Update task properties',
      enabled: Boolean(onBulkUpdate)
    },
    {
      key: 'delete' as const,
      label: 'Delete',
      icon: IconTrash,
      description: 'Delete selected tasks',
      enabled: Boolean(onBulkDelete)
    },
    {
      key: 'move' as const,
      label: 'Move',
      icon: IconArrowsMove,
      description: 'Move to another folder',
      enabled: Boolean(onBulkMove)
    },
    {
      key: 'copy' as const,
      label: 'Copy',
      icon: IconCopy,
      description: 'Copy to another folder',
      enabled: Boolean(onBulkCopy)
    }
  ].filter(action => action.enabled)

  // Sample folders/projects for demonstration
  const availableFolders = [
    { id: 'folder1', name: 'Frontend Development' },
    { id: 'folder2', name: 'Backend Development' },
    { id: 'folder3', name: 'Design Tasks' },
    { id: 'folder4', name: 'Marketing' }
  ]

  const fallbackMembersQuery = useQuery({
    ...spaceMembersQueryOptions(spaceId),
    enabled: !spacePage && Boolean(spaceId)
  })
  const availableUsers = useMemo(() => {
    const members = spacePage?.members ?? fallbackMembersQuery.data ?? []

    return members.map(member => ({
      id: member.user_id,
      name: member.display_name || member.email || 'Unknown'
    }))
  }, [fallbackMembersQuery.data, spacePage])

  useEffect(() => {
    const firstSupportedAction = supportedActions[0]?.key

    if (firstSupportedAction && !supportedActions.some(action => action.key === activeAction)) {
      setActiveAction(firstSupportedAction)
    }
  }, [activeAction, supportedActions])

  const handleFieldToggle = (field: string) => {
    const newFields = new Set(fieldsToUpdate)
    if (newFields.has(field)) {
      newFields.delete(field)
      // Remove the field from updates when unchecked
      const newUpdates = { ...updates }
      delete newUpdates[field as keyof Task]
      setUpdates(newUpdates)
    } else {
      newFields.add(field)
    }
    setFieldsToUpdate(newFields)
  }

  const handleUpdateChange = (
    field: keyof Task,
    value: Task[keyof Task] | undefined
  ) => {
    setUpdates(prev => ({ ...prev, [field]: value }))
  }

  const handleBulkEdit = async () => {
    if (fieldsToUpdate.size === 0) {
      toast.error('Please select at least one field to update')
      return
    }

    const filteredUpdates: Partial<Task> = {}
    fieldsToUpdate.forEach(field => {
      if (updates[field as keyof Task] !== undefined) {
        filteredUpdates[field as keyof Task] = updates[field as keyof Task]
      }
    })

    // Handle special cases
    if (fieldsToUpdate.has('tags') && newTags) {
      const tagsArray = newTags.split(',').map(tag => tag.trim()).filter(Boolean)
      
      switch (tagAction) {
        case 'add':
          // Add new tags to existing ones
          filteredUpdates.tags = [...(selectedTasks[0]?.tags || []), ...tagsArray]
          break
        case 'remove':
          // Remove specified tags
          filteredUpdates.tags = (selectedTasks[0]?.tags || []).filter(tag => !tagsArray.includes(tag))
          break
        case 'replace':
          // Replace all tags
          filteredUpdates.tags = tagsArray
          break
      }
    }

    if (fieldsToUpdate.has('due_date') && dateToSet) {
      filteredUpdates.due_date = dateToSet.toISOString()
    }

    await onBulkUpdate?.(filteredUpdates)
    toast.success(`Updated ${selectedTasks.length} tasks`)
    onClose()
  }

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedTasks.length} tasks? This action cannot be undone.`)) {
      return
    }
    
    await onBulkDelete?.(selectedTasks.map(task => task.id))
    toast.success(`Deleted ${selectedTasks.length} tasks`)
    onClose()
  }

  const handleBulkMove = async () => {
    if (!destinationFolder) {
      toast.error('Please select a destination folder')
      return
    }

    await onBulkMove?.(selectedTasks.map(task => task.id), destinationFolder)
    toast.success(`Moved ${selectedTasks.length} tasks`)
    onClose()
  }

  const handleBulkCopy = async () => {
    if (!destinationFolder) {
      toast.error('Please select a destination folder')
      return
    }

    await onBulkCopy?.(selectedTasks.map(task => task.id), destinationFolder)
    toast.success(`Copied ${selectedTasks.length} tasks`)
    onClose()
  }

  const getActionHandler = () => {
    switch (activeAction) {
      case 'edit': return handleBulkEdit
      case 'delete': return handleBulkDelete
      case 'move': return handleBulkMove
      case 'copy': return handleBulkCopy
    }
  }

  const getActionButtonText = () => {
    switch (activeAction) {
      case 'edit': return `Update ${selectedTasks.length} Tasks`
      case 'delete': return `Delete ${selectedTasks.length} Tasks`
      case 'move': return `Move ${selectedTasks.length} Tasks`
      case 'copy': return `Copy ${selectedTasks.length} Tasks`
    }
  }

  const getActionIcon = () => {
    switch (activeAction) {
      case 'edit': return <IconEdit size={16} />
      case 'delete': return <IconTrash size={16} />
      case 'move': return <IconArrowsMove size={16} />
      case 'copy': return <IconCopy size={16} />
    }
  }

  const isActionDestructive = activeAction === 'delete'

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-dvh overflow-y-auto p-4 sm:max-h-[90vh] sm:max-w-3xl sm:p-6">
        <DialogHeader>
          <DialogTitle>Bulk Operations</DialogTitle>
          <DialogDescription>
            Perform actions on {selectedTasks.length} selected tasks
          </DialogDescription>
        </DialogHeader>

        {/* Action Selector */}
        <div className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {supportedActions.map(({ key, label, icon: Icon, description }) => (
            <div
              key={key}
              className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                activeAction === key 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setActiveAction(key as BulkAction)}
            >
              <div className="flex flex-col items-center text-center">
                <Icon size={20} className={activeAction === key ? 'text-primary' : 'text-muted-foreground'} />
                <span className="mt-1 text-sm font-medium">{label}</span>
                <span className="text-muted-foreground mt-1 text-xs">{description}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Selected Tasks Preview */}
        <div className="mb-6">
          <Label className="text-sm font-medium">Selected Tasks ({selectedTasks.length})</Label>
          <ScrollArea className="mt-2 h-24 rounded-lg border shadow-sm">
            <div className="space-y-1 p-3">
              {selectedTasks.map(task => (
                <div key={task.id} className="flex items-center gap-2 text-sm">
                  <IconCheck size={12} className="text-green-500" />
                  <span className="truncate">{task.title}</span>
                  <Badge variant="outline" className="text-xs">
                    {task.status.replace('_', ' ')}
                  </Badge>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Action-specific Content */}
        <div className="space-y-6">
          {activeAction === 'edit' && (
            <div className="space-y-4">
              <h4 className="font-medium">Select fields to update:</h4>
              
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Status */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="status"
                      checked={fieldsToUpdate.has('status')}
                      onCheckedChange={() => handleFieldToggle('status')}
                    />
                    <Label htmlFor="status">Status</Label>
                  </div>
                  {fieldsToUpdate.has('status') && (
                    <Select
                      value={updates.status || ''}
                      onValueChange={(value) => handleUpdateChange('status', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableStatuses.map((statusValue) => {
                          const statusColumn = statusTemplate?.statuses?.find(s => 
                            s.statusValues?.includes(statusValue) || s.id === statusValue
                          )
                          const label = statusColumn?.name || statusValue.charAt(0).toUpperCase() + statusValue.slice(1).replace(/_/g, ' ')
                          return (
                            <SelectItem key={statusValue} value={statusValue}>
                              {label}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="priority"
                      checked={fieldsToUpdate.has('priority')}
                      onCheckedChange={() => handleFieldToggle('priority')}
                    />
                    <Label htmlFor="priority">Priority</Label>
                  </div>
                  {fieldsToUpdate.has('priority') && (
                    <Select
                      value={updates.priority || ''}
                      onValueChange={(value) => handleUpdateChange('priority', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Assignee */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="assigned_to"
                      checked={fieldsToUpdate.has('assigned_to')}
                      onCheckedChange={() => handleFieldToggle('assigned_to')}
                    />
                    <Label htmlFor="assigned_to">Assignee</Label>
                  </div>
                  {fieldsToUpdate.has('assigned_to') && (
                    <Select
                      value={updates.assigned_to || ''}
                      onValueChange={(value) => {
                        const user = availableUsers.find(u => u.id === value)
                        handleUpdateChange('assigned_to', value)
                        handleUpdateChange('assigned_to_name', user?.name)
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select assignee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Unassigned</SelectItem>
                        {availableUsers.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Due Date */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="due_date"
                      checked={fieldsToUpdate.has('due_date')}
                      onCheckedChange={() => handleFieldToggle('due_date')}
                    />
                    <Label htmlFor="due_date">Due Date</Label>
                  </div>
                  {fieldsToUpdate.has('due_date') && (
                    <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <IconCalendar size={16} className="mr-2" />
                          {dateToSet ? format(dateToSet, 'MMM d, yyyy') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={dateToSet}
                          onSelect={(date) => {
                            setDateToSet(date)
                            setIsDatePickerOpen(false)
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="tags"
                    checked={fieldsToUpdate.has('tags')}
                    onCheckedChange={() => handleFieldToggle('tags')}
                  />
                  <Label htmlFor="tags">Tags</Label>
                </div>
                {fieldsToUpdate.has('tags') && (
                  <div className="space-y-2">
                    <Select
                      value={tagAction}
                      onValueChange={(value) =>
                        setTagAction(value as typeof tagAction)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="add">Add tags</SelectItem>
                        <SelectItem value="remove">Remove tags</SelectItem>
                        <SelectItem value="replace">Replace all tags</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Enter tags separated by commas"
                      value={newTags}
                      onChange={(e) => setNewTags(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {(activeAction === 'move' || activeAction === 'copy') && (
            <div className="space-y-4">
              <h4 className="font-medium">
                Select destination folder:
              </h4>
              <Select value={destinationFolder} onValueChange={setDestinationFolder}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a folder" />
                </SelectTrigger>
                <SelectContent>
                  {availableFolders.map(folder => (
                    <SelectItem key={folder.id} value={folder.id}>
                      <div className="flex items-center gap-2">
                        <IconFolder size={14} />
                        {folder.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {activeAction === 'delete' && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <IconAlertTriangle size={16} className="text-red-500" />
                <span className="font-medium text-red-700">Warning</span>
              </div>
              <p className="text-sm text-red-600">
                This will permanently delete {selectedTasks.length} tasks. This action cannot be undone.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={() => void getActionHandler()()} 
            variant={isActionDestructive ? "destructive" : "default"}
          >
            {getActionIcon()}
            <span className="ml-2">{getActionButtonText()}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
